import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onClick }) => {
  return (
    <div className="border-b border-slate-200">
      <button
        onClick={onClick}
        className="w-full py-6 flex justify-between items-center text-left hover:text-slate-600 transition-colors group"
      >
        <h2 className="text-xl font-serif text-slate-900 group-hover:text-slate-600 transition-colors">{title}</h2>
        <ChevronDown 
          className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] pb-8 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed font-light">
          {children}
        </div>
      </div>
    </div>
  );
};

export default function Services() {
  const [openIndex, setOpenIndex] = useState(-1);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className="pt-8 md:pt-12 pb-24 animate-in fade-in duration-1000">
      <div className="max-w-5xl mx-auto px-6">
        <header className="mb-12">
          <h1 className="text-3xl md:text-4xl font-serif text-slate-900 mb-8">Disse services tilbydes</h1>
        </header>

        <div className="mb-20 border-t border-slate-200">
          <AccordionItem 
            title="Byggeledelse" 
            isOpen={openIndex === 0} 
            onClick={() => toggleAccordion(0)}
          >
            <p>
              SRI tilbyder byggeledelse i forbindelse med udførelse af byggeriet, hvilket omfatter koordinering af de enkelte arbejder, styring af sikkerhed, tidsplan og økonomi samt afholdelse af byggemøder – kort sagt styring af byggeriet under udførelsen.
            </p>
          </AccordionItem>

          <AccordionItem 
            title="Bygherretilsyn" 
            isOpen={openIndex === 1} 
            onClick={() => toggleAccordion(1)}
          >
            <div className="space-y-4">
              <p>
                Byggeriet er gennemprojekteret, tegninger, beskrivelser og beregninger foreligger, priser er indhentet, entreprisekontakterne er på plads og byggeriet går i gang i marken. Entreprenører og håndværkere har hver deres opgaver og overtager hinandens arbejder, men sikre at misforståelser og fejl bliver fanget på pladsen?
              </p>
              <p>
                SRI anbefaler et organiseret bygherretilsyn, da det højner kvaliteten og er til stor tilfredsstillelse for såvel for bygherre som håndværkere.
              </p>
              <p>
                SRI udarbejder en tilsynsplan i forbindelse med projekteringen, hvis bygherren ønsker det, som sikre at kritiske områder i byggeriet bliver kontrolleret. Et tilsyn dokumenteres i form af rapport med tilhørende billeder.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem 
            title="Totalrådgivning" 
            isOpen={openIndex === 2} 
            onClick={() => toggleAccordion(2)}
          >
            <div className="space-y-4">
              <p>
                Totalrådgivning betyder, at du som kunde overdrager projektering, styring og koordinering af alle byggeriets faser fra skitseforslag til færdig byggeri til totalrådgiveren. Fordelen er at du kan fokusere på egne ønsker og behov.
              </p>
              <p>
                Hvis du har planer om at bygge, kan SRI være behjælpelig med udarbejdelse af de nødvendige tegninger, beskrivelser og beregninger samt styring af projektet fra start til slut. Byggeri kan være komplekst og meget tidskrævende, og kan i sidste ende med at blive for dyrt, hvis der mangler entydigt pris- og byggegrundlag og styring af både tid, kvalitet og økonomi.
              </p>
              <p>
                SRI henter hjælp hos faste samarbejdspartnere efter behov (arkitekt, brand, VVS-, ventilation- og elingenører samt akustikker m.fl.)
              </p>
            </div>
          </AccordionItem>

          <AccordionItem 
            title="Projektering af afløb og regnvand" 
            isOpen={openIndex === 3} 
            onClick={() => toggleAccordion(3)}
          >
            <div className="space-y-4">
              <p>
                SRI har stor erfaring med projektering af afløb fra småhuse, industrivirksomheder, storkøkkener mv.
              </p>
              <p>
                Bygningsmyndighederne stiller ofte krav til max. udledning af regnvand eller krav om nedsivning på egen grund. SRI har erfaring i forsinkelsesanlæg for regnvandsinstallationer og nedsivningsanlæg.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem 
            title="Projektering af bærende konstruktioner" 
            isOpen={openIndex === 4} 
            onClick={() => toggleAccordion(4)}
          >
            <div className="space-y-4">
              <p>
                Projektering af bærende konstruktioner er et af SRI´s kerneområder.
              </p>
              <p>
                Et konstruktionsprojekt består typisk af plantegninger med tilhørende detaljetegninger af de forskellige bygningsdele samt beskrivelser og beregninger.
              </p>
              <p>
                Et projekt kan f.eks. indeholde følgende tegninger: Pæleplan, fundamentsplan, dækplan, spærplan og søjle/bjælkeplan med tilhørende detaljer.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem 
            title="Statisk dokumentation (BR18)" 
            isOpen={openIndex === 5} 
            onClick={() => toggleAccordion(5)}
          >
            <div className="space-y-8">
              <div>
                <p className="mb-4">Kravet til den tekniske dokumentation er blevet skærpet med det nye bygningsreglemenet, BR18. Dokumentationskravet fremgår af BR18 kapitel 28, 30 og 31.</p>
                
                <h3 className="text-lg font-medium text-slate-900 mb-4">Hvad er det?</h3>
                <p className="mb-4">
                  Den statiske dokumentation består af følgende dokumenter, som ifølge BR18 skal udarbejdes i alle byggesager (i Konstruktionsklasse 1 dog kun A1, A2, B1), hvori der indgår bærende konstruktioner:
                </p>
                <ul className="space-y-4 list-none p-0">
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">A1</span>
                    <span><strong className="text-slate-900">Konstruktionsgrundlag</strong> – som beskriver sikkerhedsniveau, bygningens statiske system, materialer, laster og lastkombinationer. Dokumentet danner grundlag for de statiske beregninger samt leverandørberegningerne.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">A2</span>
                    <span><strong className="text-slate-900">Statiske beregninger</strong> – beregninger, som dokumenterer bygningens styrke og stabilitet.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">A3</span>
                    <span><strong className="text-slate-900">Konstruktionstegninger og modeller</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">A4</span>
                    <span><strong className="text-slate-900">Konstruktionsændringer</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">B1</span>
                    <span><strong className="text-slate-900">Statisk projektredegørelse</strong> – indeholder dokumentation for den gennemførte kontrol af projekteringen</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">B2</span>
                    <span><strong className="text-slate-900">Statisk kontrolplan</strong> – angiver omfang og krav til kontrollen af projekteringen og udførelsen</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-medium text-slate-900 shrink-0">B3</span>
                    <span><strong className="text-slate-900">Statisk kontrolrapport</strong> – dokumenterer kontrollen af projekering og udførelse</span>
                  </li>
                </ul>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Er det nu også nødvendigt?</h3>
                <div className="space-y-4">
                  <p>
                    For fritliggende én-familiehuse og andre mindre og ukomplicerede bygninger er det i dag tilstrækkeligt at indsende materiale, som viser bygningens placering på grunden mm., for at få en byggetilladelse – altså umiddelbart ingen krav om teknisk dokumentation – men inden bygningen må tages i brug, skal der indhentes en ibrugtagningstilladelse hos bygningsmyndighederne.
                  </p>
                  <p>
                    I den forbindelse skal ansøgeren indsende “Erklæring for teknisk dokumentation”, hvor ansøger med sin underskrift erklærer at byggeriet er opført efter Bygningsreglementets bestemmelser. Altså skal beregningerne foreligge inden bygningen må tages i brug.
                  </p>
                  <p>
                    SRI anbefaler, at der altid udarbejdes statiske dokumentation inden byggeriet opføres, undtagen hvis der er tale om udhuse, garager, carport og overdækninger.
                  </p>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Hvad koster det?</h3>
                <div className="space-y-4">
                  <p>
                    Prisen for den statiske dokumentation varierer meget, da det afhænger af bygningens kompleksitet, materialer og grundlagsmaterialets detaljeringsgrad.
                  </p>
                  <p>
                    Prisen for statiske dokumentation begynder ved 10.000kr eks. moms for et simpelt rektangulær én-planhus. SRI fremsender gerne tilbud på den statiske dokumentation, men tilbyder også beregninger udarbejdet efter medgået tid til en fast timesats med et aftalt max. beløb.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>
      </div>
    </div>
  );
}
